class ProductFamilySwitcher {
  constructor(root) {
    this.root = root
    this.sectionId = root.dataset.sectionId
    this.sectionWrapperId = `shopify-section-${this.sectionId}`
    this.buttons = Array.from(root.querySelectorAll('[data-family-target]'))
    this.isLoading = false

    this.onClick = this.onClick.bind(this)
    this.onPopState = this.onPopState.bind(this)

    for (const button of this.buttons) {
      button.addEventListener('click', this.onClick)
    }

    window.addEventListener('popstate', this.onPopState)
  }

  destroy() {
    for (const button of this.buttons) {
      button.removeEventListener('click', this.onClick)
    }

    window.removeEventListener('popstate', this.onPopState)
  }

  async onClick(event) {
    event.preventDefault()

    const button = event.currentTarget
    const targetUrl = button.dataset.url

    if (!targetUrl || this.isLoading || this.isCurrent(button)) {
      return
    }

    await this.swapSection(targetUrl, true)
  }

  async onPopState() {
    await this.swapSection(window.location.href, false)
  }

  isCurrent(button) {
    return button.getAttribute('aria-pressed') === 'true'
  }

  setLoadingState(isLoading) {
    this.isLoading = isLoading
    this.root.classList.toggle('is-loading', isLoading)

    for (const button of this.buttons) {
      button.disabled = isLoading
      button.classList.toggle('is-loading', isLoading)
    }
  }

  buildSectionUrl(rawUrl) {
    const url = new URL(rawUrl, window.location.origin)
    url.searchParams.set('section_id', this.sectionId)
    return url.toString()
  }

  async fetchSectionHtml(rawUrl) {
    const response = await fetch(this.buildSectionUrl(rawUrl), {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch section HTML: ${response.status}`)
    }

    return response.text()
  }

  replaceSection(html) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const incomingSection = doc.getElementById(this.sectionWrapperId)
    const currentSection = document.getElementById(this.sectionWrapperId)

    if (!incomingSection || !currentSection) {
      throw new Error('Could not locate product section wrapper during swap')
    }

    currentSection.replaceWith(incomingSection)
  }

  async swapSection(rawUrl, pushToHistory = true) {
    try {
      this.setLoadingState(true)

      const absoluteUrl = new URL(rawUrl, window.location.origin).toString()
      const html = await this.fetchSectionHtml(absoluteUrl)

      this.replaceSection(html)

      if (pushToHistory) {
        window.history.pushState({}, '', absoluteUrl)
      }

      window.dispatchEvent(
        new CustomEvent('product:family:swapped', {
          detail: {
            url: absoluteUrl,
            sectionId: this.sectionId
          }
        })
      )

      initProductFamilySwitchers()
    } catch (error) {
      console.error('Product family switch failed:', error)
      window.location.href = rawUrl
    } finally {
      this.setLoadingState(false)
    }
  }
}

let familySwitcherInstances = []

function initProductFamilySwitchers() {
  for (const instance of familySwitcherInstances) {
    instance.destroy()
  }

  familySwitcherInstances = []

  const roots = document.querySelectorAll('[data-product-family-switcher]')

  for (const root of roots) {
    familySwitcherInstances.push(new ProductFamilySwitcher(root))
  }
}

document.addEventListener('DOMContentLoaded', initProductFamilySwitchers)
document.addEventListener('shopify:section:load', initProductFamilySwitchers)